import { useState } from "react";
import type { KnowledgeFile } from "../../types";

interface Props {
  files: KnowledgeFile[];
  onDelete?: (filename: string) => void;
}

function formatSize(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}

const statusStyles: Record<string, string> = {
  ingested: "bg-success/10 text-success",
  pending: "bg-warning/10 text-warning animate-pulse-soft",
  error: "bg-error/10 text-error",
};

export default function FileList({ files, onDelete }: Props) {
  const [confirmTarget, setConfirmTarget] = useState<string | null>(null);

  if (files.length === 0) {
    return (
      <p className="text-text-tertiary italic text-sm text-center py-10">
        Nothing here yet — add a .md or .txt file, or a YouTube video, below.
      </p>
    );
  }
  return (
    <>
    <div className="flex flex-col gap-2 mb-10">
      {files.map((f) => (
        <div
          key={f.filename}
          className="flex items-center gap-3 bg-surface border border-border-soft rounded-[10px] px-4 py-3 text-sm"
        >
          <span className="flex-1 font-medium text-ink truncate">{f.filename}</span>
          <span className="text-text-tertiary text-xs font-mono tabular-nums flex-shrink-0">{formatSize(f.size_bytes)}</span>
          <span className={`text-[0.625rem] uppercase tracking-[0.1em] py-1 px-2.5 rounded-full font-semibold flex-shrink-0 ${statusStyles[f.status] || statusStyles.error}`}>
            {f.status}
          </span>
          {onDelete && (
            <button
              aria-label={`Delete ${f.filename}`}
              className="flex-shrink-0 p-1.5 rounded-md text-text-tertiary transition-colors duration-200 cursor-pointer hover:text-error hover:bg-error/10"
              onClick={() => setConfirmTarget(f.filename)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          )}
        </div>
      ))}
    </div>

    {confirmTarget !== null && (
      <div
        className="fixed inset-0 bg-[rgba(36,28,18,0.45)] z-[300] flex items-center justify-center animate-fade-in"
        onClick={() => setConfirmTarget(null)}
      >
        <div
          className="card max-w-[420px] w-[90vw] px-8 py-9 text-center animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="mb-2">Delete this item?</h3>
          <p className="text-text-secondary text-[0.9375rem] mb-6 leading-relaxed">
            <span className="font-medium text-ink">{confirmTarget}</span> will be permanently removed from your library.
          </p>
          <div className="flex justify-center gap-3">
            <button className="btn-quiet" onClick={() => setConfirmTarget(null)}>
              Cancel
            </button>
            <button
              className="inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-medium bg-error text-cream border border-error transition-all duration-200 cursor-pointer hover:bg-[#93312a] active:scale-[0.98]"
              onClick={() => { onDelete?.(confirmTarget!); setConfirmTarget(null); }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
